import {History} from 'history';
import {get, isEmpty} from 'lodash-es';
import moment from 'moment';
import {Moment} from 'moment/moment';

import {IFieldAction, EFieldActionType, IFieldMeta} from 'entities/metadata';
import {TEntity, TEntityValue} from 'modules/data';
import {IDefaultTableState, IDefaultTableStateFlat} from 'modules/data/data-types';
import {DATE, RESOLVED_FORMATS} from 'shared/constants/date-format';
import {performRequest} from 'shared/utils';
import {combineString} from 'shared/utils/combine-string';
import {IQueryParam} from 'shared/utils/query-params/query-params-types';

export const createTableCellClassNameResolver = (entityName: string, fieldKey: string) => {
    const entityNameWithDashes = entityName.split('.').join('-');
    const fieldKeyWithDashes = fieldKey.split('.').join('-');
    return `${entityNameWithDashes}__table-cell__${fieldKeyWithDashes}`;
};

export const createDefaultSorter = (field: string, stringStructure?: string) => (
    itemA: Record<string, any>,
    itemB: Record<string, any>,
) => {
    const structure = stringStructure?.split(/[.\n]+/);
    if (structure) {
        let entryA = itemA?.[field] || '';
        let entryB = itemB?.[field] || '';
        if (!entryA) {
            structure.forEach(elem => {
                entryA += `${itemA?.[elem]}`;
                entryB = entryA;
            });
        }
        let entryAValue = '';
        let entryBValue = '';
        if (structure.length === 1) {
            entryAValue = entryA?.[structure[0]] || '';
            entryBValue = entryB?.[structure[0]] || '';
            return entryAValue.localeCompare(entryBValue);
        }
        if (structure.length === 2) {
            entryAValue = itemA?.[structure[0]]?.[structure[1]] || '';
            entryBValue = itemB?.[structure[0]]?.[structure[1]] || '';
            return entryAValue.localeCompare(entryBValue);
        }
        if (structure) {
            entryAValue = (entryA[0] && entryA[0]?.[structure[0]]) ? entryA[0]?.[structure[0]] : '';
            entryBValue = (entryB[0] && entryB[0]?.[structure[0]]) ? entryB[0]?.[structure[0]] : '';
            return entryAValue.localeCompare(entryBValue);
        }

        if (typeof entryA === 'string') {
            return entryA.localeCompare(entryB);
        }

        if (typeof entryA === 'number') {
            return entryA - entryB;
        }
    }

    return 0;
};

export const createStringDate = (value: TEntityValue, dateFormat?: string) => {
    const date = (value && moment(value as string | Moment, RESOLVED_FORMATS)) as Moment;

    return !isEmpty(date) && date?.isValid()
        ? date?.format(dateFormat || DATE)
        : value;
};

const CLOSED_LOOKUP_CODE = 'CLOSED';

/**
 * Проверяет, истек ли срок, указанный в поле с типом DATE_PASSED_INDICATOR.
 * У завершенных запросов срок не считается истекшим и не подсвечивается
 * @param value - значение поля с датой
 * @param record - запись таблицы
 */
export const isDatePassed = (value: TEntityValue, record: TEntity) => {
    if (typeof value !== 'string') {
        return false;
    }

    if (get(record, 'topicStatus.lookupCode') === CLOSED_LOOKUP_CODE) {
        return false;
    }

    return new Date(value) < new Date();
};

export const callRedirect = (
    record: Record<string, any>,
    updateQueryParams: (params: IQueryParam[]) => void,
    linkField?: string,
    history?: History,
    url?: string,
    userId?: number,
) => {
    if (linkField === 'notificationId') {
        const value = record?.notificationAssignments?.find(
            (item: {
                assigneeId: number;
                read: boolean;
            }) => item?.assigneeId === userId && !item.read,
        );
        if (value) {
            performRequest({
                url: `${url}/mark-as-read/notification`,
                data: JSON.stringify({notificationId: record?.notificationId, userId}),
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
            }).then(data => {
                if (data.status === 200 && history) {
                    updateQueryParams([{name: 'entity', value: record[linkField]}]);
                }
            });
        } else if (linkField && history) {
            updateQueryParams([{name: 'entity', value: record[linkField]}]);
        }
    } else if (linkField && history) {
        updateQueryParams([{name: 'entity', value: record[linkField]}]);
    }
};
export const createStringCell = (value: any[], stringStructure?: string) => {
    if (stringStructure) {
        return value.map(item => combineString(stringStructure, item))?.join(', ');
    }
    return value.join(', ');
};

export const getAllowedActions = (actions?: IFieldAction[]) => {
    const allowedActions: EFieldActionType[] = [];
    if (actions && actions.length > 0) {
        actions.forEach(action => {
            if (action.name) {
                allowedActions.push(action.name);
            }
        });
    }
    return allowedActions;
};

export const convertFlatObjectToTableState = (
    tableStateFlat: IDefaultTableStateFlat, total?: number,
): IDefaultTableState => {
    const {
        paginationPageSize, paginationCurrent, paginationTotal, sorterField, sorterOrder,
    } = tableStateFlat || {};

    return {
        pagination: {
            pageSize: paginationPageSize,
            current: paginationCurrent,
            total: total ?? paginationTotal,
        },
        sorter: {
            field: sorterField,
            order: sorterOrder,
        },
    };
};

/**
 * Создает объект с пустыми значениями для видимых полей по ID записей
 * @param data - Массив сущностей с id
 * @param fields - Метаданные полей
 * @returns Объект вида {[id]: {[fieldKey]: {}}}
 */
export const createEmptyObjectsById = (
    data?: TEntity[],
    fields?: IFieldMeta[],
): Record<number, Record<string, {}>> => {
    if (!data || !fields) return {};

    const result: Record<number, Record<string, {}>> = {};

    // Получаем только ключи видимых полей из меты
    const fieldKeys = fields.reduce<string[]>((acc, field) => {
        if (!field.isHidden) acc.push(field.key);
        return acc;
    }, []);

    data.forEach(item => {
        const emptyObject: Record<string, {}> = {};

        fieldKeys.forEach(key => {
            emptyObject[key] = {};
        });

        result[item.id as number] = emptyObject;
    });

    return result;
};

/**
 * Проверяет, являются ли значения указанного ключа уникальными в массиве данных
 * @param data Массив объектов данных
 * @param key Ключ, по которому проверяется уникальность
 * @returns true если все значения ключа уникальны, false если есть дубликаты
 */
export const checkUniquenessByKey = <T extends Record<string, TEntityValue>>(
    data: T[],
    key: keyof T
): boolean => {
    if (!Array.isArray(data) || data.length === 0) {
        return true;
    }

    const uniqueValues = new Set<any>();

    return !data.some(item => {
        const value = item[key];

        if (uniqueValues.has(value)) {
            return true;
        }

        uniqueValues.add(value);
        return false;
    });
}
